/**
 * Interactive breakpoint/step debugger for a Logic/Compute Query (ADR-0056, S4).
 *
 * A Monaco editor with a breakpoint GUTTER (click the glyph margin to toggle), a
 * current-line highlight while paused, step controls, a variables panel, and the
 * terminated result. Drives the debug WebSocket via `QueryDebugSession`. The run is
 * DRY-RUN server-side (a stepped `logic` write is rolled back), so it's side-effect-free.
 */

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Editor, { type Monaco } from "@monaco-editor/react";
import {
  Bug,
  ChevronsRight,
  CornerDownRight,
  CornerLeftUp,
  Play,
  RotateCcw,
  Square,
  X,
} from "lucide-react";

import { useAuthStore } from "@/lib/auth";
import { useTheme } from "@/theme/ThemeProvider";
import {
  QueryDebugSession,
  debugSocketUrl,
  fetchBreakpointLines,
  type DebugEvent,
  type DebugVar,
  type StepKind,
} from "@/lib/queryDebug";
import { buildLineMapper, type LineMapper } from "@/lib/sourceMap";

type Status = "idle" | "connecting" | "running" | "paused" | "terminated" | "error";

// Derive the Monaco types from the `Editor` component (monaco-editor isn't a direct
// dep — same trick `CodeEditor.tsx` uses to type `onMount`).
type EditorInstance = Parameters<NonNullable<React.ComponentProps<typeof Editor>["onMount"]>>[0];
type MouseEvt = Parameters<Parameters<EditorInstance["onMouseDown"]>[0]>[0];
type Decoration = Parameters<EditorInstance["deltaDecorations"]>[1][number];
// Hover-provider param types, derived from the Monaco types (no direct monaco-editor dep).
type HoverProvider = Parameters<Monaco["languages"]["registerHoverProvider"]>[1];
type HoverModel = Parameters<NonNullable<HoverProvider["provideHover"]>>[0];
type HoverPos = Parameters<NonNullable<HoverProvider["provideHover"]>>[1];

export function QueryDebugPanel({
  appId,
  queryId,
  code,
  codeTs,
  input,
  onClose,
}: {
  appId: string;
  queryId: string;
  /** The transpiled JS QuickJS runs (the engine's line coordinate) — the SAVED `spec.code`. */
  code: string;
  /** The TypeScript source (`code_ts`). When present, the panel RE-COMPILES it to derive a
   *  TS→JS source map and — only if the recompiled JS matches the saved `code` — shows the TS
   *  and translates every line JS↔TS (browser-devtools model). Any mismatch (unsaved edit) or a
   *  plain-JS body → the JS is shown + stepped directly. No persistence: the map is ephemeral. */
  codeTs?: string | null;
  /** The Prova/contract values — passed as `ctx.input` to the stepped body. */
  input?: Record<string, unknown> | null;
  onClose: () => void;
}) {
  const token = useAuthStore((s) => s.token);
  const { resolved } = useTheme();
  // TS-debug: recompile `codeTs` to derive the line map (async — `compileAppLogic` lazy-loads
  // `typescript`). Gated on the recompiled JS === the saved `code` the backend debugs, so the
  // map's JS lines can't drift from what's stepped. `tsMode` when it yields an inlined TS source.
  const [mapper, setMapper] = useState<LineMapper | null>(null);
  const mapperRef = useRef<LineMapper | null>(null);
  mapperRef.current = mapper;
  useEffect(() => {
    let alive = true;
    if (!codeTs) {
      setMapper(null);
      return;
    }
    (async () => {
      try {
        const { compileAppLogic } = await import("@/lib/appLogicTs");
        const res = await compileAppLogic(codeTs);
        if (!alive) return;
        // Only map when the recompiled JS is byte-identical to the saved code the engine steps.
        setMapper(res.ok && res.codeJs === code ? buildLineMapper(res.sourceMap) : null);
      } catch {
        if (alive) setMapper(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [codeTs, code]);
  const tsMode = !!mapper?.sourceText;
  const displayCode = tsMode ? (mapper as LineMapper).sourceText! : code;
  // Starts IDLE — the developer sets breakpoints first, then ▶ Avvia connects + runs.
  const [status, setStatus] = useState<Status>("idle");
  const [started, setStarted] = useState(false);
  const [line, setLine] = useState<number | null>(null);
  const [vars, setVars] = useState<DebugVar[]>([]);
  const [result, setResult] = useState<{ ok: boolean; value: unknown; error: string | null } | null>(
    null,
  );
  const [message, setMessage] = useState<string>("");
  // Bumped by "Riavvia" to tear down the finished session and open a fresh one.
  const [runNonce, setRunNonce] = useState(0);
  // The `ctx.input` for the run — editable IN the panel, seeded from the Prova values.
  // Editing it takes effect on the next Riavvia (it's read at connect time via the ref).
  const [inputText, setInputText] = useState(() => JSON.stringify(input ?? {}, null, 2));
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;

  const sessionRef = useRef<QueryDebugSession | null>(null);
  const editorRef = useRef<EditorInstance | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const bpRef = useRef<Set<number>>(new Set());
  // Hover-inspector (VS Code-style): the Monaco hover provider (registered once at mount)
  // reads the current frame vars + paused state through refs, so it never re-binds.
  const hoverDisposeRef = useRef<{ dispose: () => void } | null>(null);
  const varsRef = useRef<DebugVar[]>([]);
  const pausedRef = useRef(false);
  varsRef.current = vars;
  pausedRef.current = status === "paused";
  // The editor lines a breakpoint can bind to (QuickJS pc2line). `null` = not yet
  // fetched OR the debug engine can't extract them → allow any line (the debugger
  // ignores an unbindable breakpoint anyway). A ref so the Monaco mouse handler reads
  // it without re-binding, + state to re-render the gutter hints.
  const execLinesRef = useRef<Set<number> | null>(null);
  const [execLines, setExecLines] = useState<Set<number> | null>(null);
  // The RAW JS executable lines from the backend (null = not fetched / unconfined). The
  // display (TS) confine set is DERIVED from these + the mapper, so it re-maps whenever the
  // async mapper resolves (fetch + compile race independently).
  const [jsExecLines, setJsExecLines] = useState<number[] | null>(null);

  // Fetch the executable lines once per query so a breakpoint can't sit on a blank /
  // non-executable line. Graceful: any failure leaves it null → allow any line.
  useEffect(() => {
    let alive = true;
    setJsExecLines(null);
    fetchBreakpointLines(appId, queryId)
      .then((res) => {
        if (alive && res.available) setJsExecLines(res.lines);
      })
      .catch(() => {
        /* old engine / no access → leave unconfined */
      });
    return () => {
      alive = false;
    };
  }, [appId, queryId]);

  // Derive the DISPLAY confine set: JS lines in JS mode, mapped to TS lines in TS mode.
  // Re-runs when the mapper resolves so the gutter is confined in the shown coordinate.
  useEffect(() => {
    if (jsExecLines == null) {
      execLinesRef.current = null;
      setExecLines(null);
      return;
    }
    const display = tsMode ? (mapper as LineMapper).mapExecutableLines(jsExecLines) : jsExecLines;
    const set = new Set(display);
    execLinesRef.current = set;
    setExecLines(set);
  }, [jsExecLines, mapper, tsMode]);

  // ---- open the session when the developer presses ▶ Avvia (via `started` + runNonce),
  // NOT on mount — so breakpoints can be placed first.
  useEffect(() => {
    if (!token || !started) return;
    // Fresh run — clear the previous session's terminal state (but KEEP the breakpoints
    // the developer placed while idle).
    setStatus("connecting");
    setLine(null);
    setVars([]);
    setResult(null);
    setMessage("");
    let parsedInput: Record<string, unknown> | null = null;
    try {
      const p = JSON.parse(inputTextRef.current || "{}");
      if (p && typeof p === "object") parsedInput = p as Record<string, unknown>;
    } catch {
      parsedInput = null; // invalid JSON → debug with empty input
    }
    const url = debugSocketUrl(appId, queryId, token, parsedInput);
    const session = new QueryDebugSession(url, {
      onEvent: (ev: DebugEvent) => {
        switch (ev.type) {
          case "ready":
            setStatus("running");
            // Push the breakpoints the developer placed while idle (mapped TS→JS).
            session.setBreakpoints(jsBreakpoints());
            break;
          case "stopped":
            // koush ALWAYS halts at `entry` first; skip it (the developer pressed Avvia
            // to RUN) so it goes straight to the first real breakpoint — or to the end.
            if (ev.reason === "entry") {
              setStatus("running");
              session.resume("continue");
              break;
            }
            setStatus("paused");
            // The engine reports a JS line; show the corresponding TS line in TS mode.
            setLine(toDisplayLine(ev.line));
            session.requestVars(); // pull frame-0 scope for the panel
            break;
          case "vars":
            setVars(ev.vars);
            break;
          case "terminated":
            setStatus("terminated");
            setResult({ ok: ev.ok, value: ev.value, error: ev.error });
            setLine(null);
            break;
          case "error":
            setStatus("error");
            setMessage(ev.error);
            break;
        }
      },
      // Only a socket that closes BEFORE it ever connected is an error (e.g. the WS
      // upgrade was dropped). A clean end (terminated), a user close, or React's
      // StrictMode dev double-mount cleanup must NOT flip to "errore".
      onClose: () => setStatus((s) => (s === "connecting" ? "error" : s)),
    });
    sessionRef.current = session;
    session.connect();
    return () => session.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, queryId, token, started, runNonce]);

  const finished = status === "terminated" || status === "error";
  const notStarted = status === "idle";
  // ▶ Avvia / ↺ Riavvia — (re)connect a fresh session and run (breakpoints kept).
  function start() {
    sessionRef.current?.close();
    setStarted(true);
    setRunNonce((n) => n + 1); // re-runs the connect effect with a fresh session
  }
  // ⏹ Ferma — stop the run and go back to idle so breakpoints can be re-edited.
  function stop() {
    sessionRef.current?.close();
    setStarted(false);
    setStatus("idle");
    setLine(null);
    setVars([]);
  }

  // ---- Monaco: breakpoint gutter + current-line highlight
  function handleMount(ed: EditorInstance, monaco: Monaco) {
    editorRef.current = ed;
    monacoRef.current = monaco;
    ed.updateOptions({ glyphMargin: true, readOnly: true });
    ed.onMouseDown((e: MouseEvt) => {
      if (e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
        const ln = e.target.position?.lineNumber;
        if (ln) toggleBreakpoint(ln);
      }
    });
    // Hover-inspector: while PAUSED, hovering an identifier shows its current value from
    // the frame scope (VS Code-style). Registered once (globally per language) but scoped
    // to THIS editor's model; reads live state via refs. Disposed on unmount.
    hoverDisposeRef.current?.dispose();
    hoverDisposeRef.current = monaco.languages.registerHoverProvider(
      ["javascript", "typescript"],
      {
        provideHover(model: HoverModel, position: HoverPos) {
          if (!pausedRef.current || model !== editorRef.current?.getModel()) return null;
          const word = model.getWordAtPosition(position);
          if (!word) return null;
          const v = varsRef.current.find((x) => x.name === word.word);
          if (!v) return null;
          return {
            range: new monaco.Range(
              position.lineNumber,
              word.startColumn,
              position.lineNumber,
              word.endColumn,
            ),
            contents: [
              { value: `**${v.name}**` },
              { value: "```json\n" + prettyVar(v.value) + "\n```" },
            ],
          };
        },
      },
    );
    renderDecorations();
  }

  // `bpRef` holds DISPLAY lines (TS lines in TS mode); the engine speaks JS lines, so
  // translate when pushing. A TS line with no JS mapping is dropped (can't bind).
  function jsBreakpoints(): number[] {
    const display = [...bpRef.current].sort((a, b) => a - b);
    const m = mapperRef.current;
    if (!m?.sourceText) return display; // JS mode — lines are already JS
    const js = new Set<number>();
    for (const t of display) {
      const j = m.tsToJs(t);
      if (j != null) js.add(j);
    }
    return [...js].sort((a, b) => a - b);
  }
  // A JS (engine) line → the DISPLAY (TS) line to highlight, or null when unmapped.
  function toDisplayLine(jsLine: number | null): number | null {
    if (jsLine == null) return null;
    const m = mapperRef.current;
    return m?.sourceText ? m.jsToTs(jsLine) : jsLine;
  }

  function toggleBreakpoint(ln: number) {
    const set = bpRef.current;
    // Confine a NEW breakpoint to an executable line (removing one is always allowed).
    // A click on a non-executable line is SILENTLY ignored — no breakpoint, no banner.
    const exec = execLinesRef.current;
    if (!set.has(ln) && exec && !exec.has(ln)) return;
    if (set.has(ln)) set.delete(ln);
    else set.add(ln);
    sessionRef.current?.setBreakpoints(jsBreakpoints()); // live update if running
    renderDecorations();
  }

  function renderDecorations() {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    if (!ed || !monaco) return;
    const decs: Decoration[] = [];
    for (const ln of bpRef.current) {
      decs.push({
        range: new monaco.Range(ln, 1, ln, 1),
        options: { isWholeLine: false, glyphMarginClassName: "tray-dbg-breakpoint" },
      });
    }
    if (line != null && line > 0) {
      decs.push({
        range: new monaco.Range(line, 1, line, 1),
        options: { isWholeLine: true, className: "tray-dbg-currentline" },
      });
    }
    decorationsRef.current = ed.deltaDecorations(decorationsRef.current, decs);
  }

  // re-render the current-line highlight whenever we stop
  useEffect(() => {
    renderDecorations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  // Dispose the (global-per-language) hover provider when the panel unmounts.
  useEffect(() => () => hoverDisposeRef.current?.dispose(), []);

  const paused = status === "paused";
  function step(kind: StepKind) {
    if (!paused) return;
    setStatus("running");
    setVars([]);
    setLine(null);
    sessionRef.current?.resume(kind);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <style>{`
        .tray-dbg-breakpoint { background: hsl(var(--danger)); border-radius: 50%;
          width: 10px !important; height: 10px !important; margin-left: 5px; margin-top: 5px; }
        .tray-dbg-currentline { background: hsl(var(--warning) / 0.18); }
      `}</style>

      {/* toolbar */}
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5 text-sm">
        <Bug className="h-4 w-4 text-danger" />
        <span className="font-medium">Debug</span>
        <span
          className="text-[11px] text-muted-foreground"
          title={
            tsMode
              ? "Debug del sorgente TypeScript via source-map (il motore esegue il JS transpilato). È un dry-run: le scritture di una Logic vengono annullate."
              : "Il debugger esegue il JS transpilato (spec.code). È un dry-run: le scritture di una Logic vengono annullate."
          }
        >
          {tsMode ? "TypeScript · dry-run" : "JS compilato · dry-run"}
        </span>
        <StatusChip status={status} line={line} />
        <div className="ml-2 flex items-center gap-1">
          {/* ▶ = Avvia when idle (run to the first breakpoint), else Continua when paused. */}
          <IconBtn
            title={notStarted ? "Avvia (esegui fino al primo breakpoint)" : "Continua (F8)"}
            onClick={notStarted ? start : () => step("continue")}
            disabled={notStarted ? false : !paused}
          >
            <Play className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step over (F10)" onClick={() => step("stepOver")} disabled={!paused}>
            <ChevronsRight className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step into (F11)" onClick={() => step("stepIn")} disabled={!paused}>
            <CornerDownRight className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step out (Shift+F11)" onClick={() => step("stepOut")} disabled={!paused}>
            <CornerLeftUp className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Ferma (torna a modifica breakpoint)" onClick={stop} disabled={notStarted}>
            <Square className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Riavvia il debug" onClick={start} disabled={!finished}>
            <RotateCcw className="h-4 w-4" />
          </IconBtn>
        </div>
        <button
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-elevated"
          title="Chiudi debug"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* code + breakpoint gutter */}
        <div className="min-h-0 min-w-0 flex-1">
          <Editor
            height="100%"
            language={tsMode ? "typescript" : "javascript"}
            value={displayCode}
            onMount={handleMount}
            theme={resolved === "dark" ? "vs-dark" : "vs"}
            options={{
              readOnly: true,
              glyphMargin: true,
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: "on",
              scrollBeyondLastLine: false,
            }}
          />
        </div>

        {/* right rail: input + variables + result */}
        <div className="flex w-72 shrink-0 flex-col border-l border-border">
          {/* INPUT — what the run receives as ctx.input; edit + Riavvia to apply. */}
          <div className="border-b border-border p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">Input · ctx.input</span>
              <span className="text-[10px] text-muted-foreground">JSON · ↺ per applicare</span>
            </div>
            <textarea
              className="h-20 w-full resize-y rounded border border-border bg-elevated px-1.5 py-1 font-mono text-[11px] outline-none focus:border-accent"
              spellCheck={false}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder='{"deal_id": "…"}'
            />
            <button
              className="mt-1 flex w-full items-center justify-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-foreground hover:bg-elevated"
              onClick={start}
              title="Avvia il debug con questo input"
            >
              {notStarted ? <Play className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}{" "}
              {notStarted ? "Avvia con questo input" : "Riavvia con questo input"}
            </button>
          </div>

          <div className="border-b border-border px-2 py-1 text-xs font-semibold text-muted-foreground">
            Variabili {paused ? `(linea ${line ?? "?"})` : ""}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {vars.length === 0 ? (
              <div className="px-1 py-1 text-xs text-muted-foreground">
                {paused ? "nessuna variabile in scope" : "in pausa a un breakpoint per ispezionare"}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {vars.map((v) => (
                  <div key={v.name} className="rounded border border-border/60 bg-elevated/40">
                    <div className="border-b border-border/50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-foreground">
                      {v.name}
                    </div>
                    <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words px-1.5 py-1 font-mono text-[11px] text-muted-foreground">
                      {prettyVar(v.value)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
          {result && (
            <div className="border-t border-border p-2 text-xs">
              <div className="mb-1 font-semibold text-muted-foreground">Risultato</div>
              {result.ok ? (
                <pre className="max-h-40 overflow-auto rounded bg-elevated p-1.5 font-mono">
                  {JSON.stringify(result.value, null, 2)}
                </pre>
              ) : (
                <div className="rounded bg-danger/10 p-1.5 font-mono text-danger">{result.error}</div>
              )}
            </div>
          )}
          {status === "error" && message && (
            <div className="border-t border-border p-2 font-mono text-xs text-danger">{message}</div>
          )}
          {notStarted && (
            <div className="border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground">
              Clicca a sinistra dei numeri di riga per i breakpoint, poi ▶ Avvia.
              {execLines && " Solo le righe eseguibili accettano un breakpoint."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** The backend sends each variable's value as a compact JSON string (or a primitive
 *  repr). Indent it for the inspector; `{}` (e.g. `ctx.db`, whose methods don't
 *  serialize) gets a hint so it doesn't read as "empty". */
function prettyVar(value: string): string {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length === 0) {
      return Array.isArray(parsed) ? "[]" : "{}  (oggetto senza campi serializzabili)";
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value; // a primitive / non-JSON repr (e.g. "undefined", "function …")
  }
}

function StatusChip({ status, line }: { status: Status; line: number | null }) {
  const map: Record<Status, { label: string; cls: string }> = {
    idle: { label: "pronto · imposta i breakpoint", cls: "bg-muted text-muted-foreground" },
    connecting: { label: "connessione…", cls: "bg-muted text-muted-foreground" },
    running: { label: "in esecuzione", cls: "bg-info/15 text-info" },
    paused: { label: `in pausa · linea ${line ?? "?"}`, cls: "bg-warning/20 text-warning" },
    terminated: { label: "terminato", cls: "bg-success/15 text-success" },
    error: { label: "errore", cls: "bg-danger/15 text-danger" },
  };
  const m = map[status];
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${m.cls}`}>{m.label}</span>;
}

function IconBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      className="rounded p-1 text-foreground hover:bg-elevated disabled:opacity-40"
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}
